// ghostRacer.ts — GHOST DUELS (Slice A) spectral replay entity. Given a decoded GhostTrack + the live
// scene, it replays a past player-0 run as a translucent muncher racing alongside you: it interpolates
// position between the recorded move events, pops a chomp + a spoiler-safe corner glyph on the cells it
// acted on, and ticks a compact score chip. It reads ONLY the track + the current sim time — it never
// touches the Sim, the board state, or any player (GHOST_DUELS_BRIEF §2, §7). All motion is gated by
// motionAllowed(); under reduced-motion positions SNAP and no tween/pop/spark runs.
//
// SPOILER STANCE (§6): glyphs appear only for cells the ghost actually acted on (ate correct ✓ / ate
// decoy ✗ / threaded an armed decoy ◇), as-they-happen — strictly less spoilery than the existing
// static challenge overlay, and it never marks a tile the ghost never touched.
//
// Ownership: everything lives under one board-space root container (depth 4.5, below AI@5 / human@6) plus
// one HUD-space chip container; destroy() kills every tween and destroys both, so a scene shutdown leaks
// nothing.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { drawRivalMuncher, type SkinLook } from './muncher';
import { motionAllowed, scaledFont } from './settings';
import type { GhostTrack } from './ghostTrack';
import { pickTaunt, spawnGhostCaption, type TauntMoment } from './ghostTaunts';

// Pale desaturated VIOLET-WHITE identity — deliberately clear of human-cyan (0x22d3ee) and
// rival-magenta (0xe879f9) so the echo never reads as either live muncher.
/** Body fill / bloom / non-WebGL tint fallback. */
const GHOST_TINT = 0xd8ccff;
/** Readable violet for the name tag + score chip (higher contrast than the near-white body fill). */
const GHOST_ACCENT = 0xb9a6ef;
/** Body alpha while MOVING vs parked (idle-dim), and the flat reduced-motion constant. */
const GHOST_ALPHA_MOVING = 0.55;
const GHOST_ALPHA_IDLE = 0.32;
const GHOST_ALPHA_STATIC = 0.4;
/** Parked-this-long → ease the body toward the idle alpha (stops it bleeding over answer text). */
const IDLE_DIM_MS = 400;
/** The ghost body renders at 0.85× the live munchers so it reads as an ECHO, not a peer. */
const GHOST_SCALE = 0.85;
/**
 * Rival-sheet spectral skin (applyCosmeticSkin WebGL path, §7). Retuned Slice D: the old {hue:-40,
 * sat:-0.55} left the raster a mid blue-violet that art-eye read as "magenta-purple" — it never lifted
 * LIGHTNESS, so it stayed a saturated ~L73 purple instead of a PALE violet-white. Color-math (Phaser
 * Display.ColorMatrix, base rival magenta 0xe879f9 ≈ HSL 292°/91/73): rotate hue −34° → violet ~253°,
 * gently desaturate (−0.2), then wash 50% toward white (bright, a hue-preserving lerp-to-white that
 * lifts L 73→90). Result ≈ rgb(214,203,255) ≈ HSL 253/100/90 — matches the non-WebGL fallback GHOST_TINT
 * (0xd8ccff) so both paths read the same pale violet-white echo, clearly paler + whiter than rival-magenta.
 */
const SPECTRAL_SKIN: SkinLook = { tint: GHOST_TINT, hue: -34, sat: -0.2, bright: 0.5 };

/** Min score gap (one decoy penalty) before a lead flip counts — silences zero-churn ties. */
const LEAD_GAP = 60;
/** Min gap between any two ghost taunts (sim ms) — the ghost reacts, it doesn't chatter. */
const TAUNT_MIN_GAP_MS = 6000;

/** Corner-glyph role → board palette colour, matching the spoiler-free ghostOverlay/share legend. */
const ROLE_COLOR = { correct: PALETTE.correct, decoy: PALETTE.hazard, threaded: PALETTE.armed } as const;
type GlyphRole = keyof typeof ROLE_COLOR;

type ArtObject = Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics;

export interface GhostRacerConfig {
  track: GhostTrack;
  /** display name (≤12 chars); 'YOU' for the local self-race. */
  name: string;
  /** the ghost's board start cell (== the live human start on the pinned same-layout board). */
  startCell: number;
  cellCenter: (cellId: number) => { x: number; y: number };
  spriteYOffset: number;
  munchRadius: number;
  cellW: number;
  cellH: number;
  /** screen anchor for the compact score chip (right-anchored: content grows left from x). */
  chipAnchor: { x: number; y: number };
  /**
   * Attach the raster rival sheet (with the spectral skin) into `container`, returning true on success.
   * GameScene wraps its private attachMuncherSprite('rival', skin); null → geometric fallback.
   */
  attachRaster?: (container: Phaser.GameObjects.Container, skin: SkinLook) => boolean;
}

export class GhostRacer {
  private readonly scene: Phaser.Scene;
  private readonly track: GhostTrack;
  private readonly cellCenter: (id: number) => { x: number; y: number };
  private readonly spriteYOffset: number;
  private readonly munchRadius: number;
  private readonly cellW: number;
  private readonly cellH: number;

  private readonly root: Phaser.GameObjects.Container;
  private readonly glyphLayer: Phaser.GameObjects.Container;
  private readonly body: Phaser.GameObjects.Container;
  /** M7: a subtle violet outline that marks the ghost's CURRENT tile ("YOUR GHOST" marker, mock 2). */
  private readonly cellRing: Phaser.GameObjects.Graphics;
  private readonly art?: ArtObject;
  private readonly artBaseScale: number;
  private readonly owned: Phaser.GameObjects.GameObject[] = [];

  private readonly chip: Phaser.GameObjects.Container;
  private readonly chipBg: Phaser.GameObjects.Graphics;
  private readonly chipName: Phaser.GameObjects.Text;
  private readonly chipScore: Phaser.GameObjects.Text;

  private cursor = 0;
  private curCell: number;
  private score = 0;
  private lastMoveMs = 0;
  private destroyed = false;

  /** S3 item 9: the ghost's current board cell, so label-occlusion can fade the word under it (the
   *  'YOUR GHOST' tag sat over the tile label). -1 once destroyed. */
  get cell(): number {
    return this.destroyed ? -1 : this.curCell;
  }

  /** FACE & FURY S5: the ghost's cumulative chip score (its munch-sum), read by the photo-finish
   *  detector to compare a duel's final margin on the SAME basis the human's track score uses. */
  get liveScore(): number {
    return this.score;
  }

  // --- taunt captions (Slice D) — the ghost "talks" via name-hash-seeded plated captions ----------
  private readonly tauntName: string;
  private readonly tauntAccent: string;
  private readonly chipAnchor: { x: number; y: number };
  private lastLeadSign = 0; // -1 human ahead, +1 ghost ahead, 0 tied (within LEAD_GAP)
  private lastTauntMs = -Infinity;
  private tauntedStart = false;
  private tauntedFinish = false;

  constructor(scene: Phaser.Scene, cfg: GhostRacerConfig) {
    this.scene = scene;
    this.track = cfg.track;
    this.cellCenter = cfg.cellCenter;
    this.spriteYOffset = cfg.spriteYOffset;
    this.munchRadius = cfg.munchRadius;
    this.cellW = cfg.cellW;
    this.cellH = cfg.cellH;
    this.curCell = cfg.startCell;
    this.tauntName = cfg.name.slice(0, 12) || 'YOUR GHOST';
    this.tauntAccent = hex(GHOST_ACCENT);
    this.chipAnchor = cfg.chipAnchor;

    // board-space root (depth below AI@5 / human@6). glyphLayer added FIRST so glyphs always sit
    // behind the ghost body; child render order is add order within a container.
    this.root = scene.add.container(0, 0).setDepth(4.5);
    this.glyphLayer = scene.add.container(0, 0);
    this.body = scene.add.container(0, 0);
    this.root.add([this.glyphLayer, this.body]);

    // M7: a subtle violet ring on the ghost's CURRENT tile so "your ghost is here" reads at a glance
    // (mock 2's YOUR GHOST marker). Outline ONLY (never a fill) so the tile's answer label stays legible
    // through it; drawn once at the graphics origin and repositioned as the ghost steps (cheap). Added
    // FIRST (behind glyphs + body); it dies with root.destroy(true), so it leaks nothing on teardown.
    this.cellRing = scene.add.graphics();
    const rw = this.cellW - 6;
    const rh = this.cellH - 6;
    this.cellRing.lineStyle(2, GHOST_ACCENT, 0.5);
    this.cellRing.strokeRoundedRect(-rw / 2, -rh / 2, rw, rh, 9);
    this.root.addAt(this.cellRing, 0);
    const ringStart = this.cellCenter(this.curCell);
    this.cellRing.setPosition(ringStart.x, ringStart.y);

    if (cfg.attachRaster && cfg.attachRaster(this.body, SPECTRAL_SKIN)) {
      this.art = this.body.getData('animSprite') as Phaser.GameObjects.Sprite | undefined;
      // attachMuncherSprite stashes the true resting scale on the sprite; fall back to its live scale.
      const rest = (this.art?.getData('restScale') as number | undefined) ?? this.art?.scale ?? 1;
      this.artBaseScale = rest * GHOST_SCALE; // echo: 0.85× the live muncher
      this.art?.setScale(this.artBaseScale);
    } else {
      const g = scene.add.graphics({ x: 0, y: this.spriteYOffset });
      drawRivalMuncher(g, this.munchRadius / 0.46, { tint: GHOST_TINT, glow: false });
      g.setScale(GHOST_SCALE);
      this.body.add(g);
      this.art = g;
      this.artBaseScale = GHOST_SCALE;
    }
    // spectral alpha on the body only (art + name tag) — glyphs keep their own fainter alpha. Under
    // reduced-motion it's a flat constant; under motion update() idle-dims it (see below).
    this.body.setAlpha(motionAllowed() ? GHOST_ALPHA_MOVING : GHOST_ALPHA_STATIC);

    // name tag above the body — 'YOUR GHOST' in readable violet disambiguates it from the live 'YOU'.
    const tag = scene.add
      .text(0, this.spriteYOffset - this.munchRadius - 5, cfg.name.slice(0, 12) || 'YOUR GHOST', {
        fontFamily: FONT.display,
        fontSize: `${Math.max(9, Math.min(13, scaledFont(11, 9)))}px`,
        color: hex(GHOST_ACCENT),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.body.add(tag);

    const start = this.cellCenter(this.curCell);
    this.body.setPosition(start.x, start.y);

    // HUD-space compact score chip (right-anchored, spectral), depth just above the board cells.
    this.chip = scene.add.container(cfg.chipAnchor.x, cfg.chipAnchor.y).setDepth(3);
    this.chipBg = scene.add.graphics();
    const fs = Math.min(15, scaledFont(12, 10));
    // S3 item 2: the HUD chip drops the redundant name — the ghost's name already rides its on-board
    // tag, so the chip is just the 👻 glyph + score (no second "YOUR GHOST" caption in two places).
    this.chipName = scene.add
      .text(0, 0, '👻', {
        fontFamily: FONT.display,
        fontSize: `${fs}px`,
        color: hex(GHOST_ACCENT),
        fontStyle: '600',
      })
      .setOrigin(1, 0.5);
    this.chipScore = scene.add
      .text(0, 0, '0', { fontFamily: FONT.display, fontSize: `${fs}px`, color: hex(GHOST_ACCENT), fontStyle: 'bold' })
      .setOrigin(1, 0.5);
    this.chip.add([this.chipBg, this.chipName, this.chipScore]);
    this.layoutChip();
  }

  /**
   * Advance the ghost to `simTimeMs`: fire every recorded event whose time has arrived (glyphs + chomp +
   * score), then resolve the position ONCE to the latest move cell (so multiple events crossing in a
   * single frame never stack overlapping position tweens).
   */
  update(simTimeMs: number, humanScore?: number): void {
    if (this.destroyed) return;
    let targetCell = this.curCell;
    let moved = false;
    let scored = false;
    while (this.cursor < this.track.events.length && this.track.events[this.cursor].t <= simTimeMs) {
      const ev = this.track.events[this.cursor];
      this.cursor += 1;
      switch (ev.kind) {
        case 'move':
          targetCell = ev.cell;
          moved = true;
          break;
        case 'munchCorrect':
          this.score += ev.value ?? 0;
          scored = true;
          this.placeGlyph(ev.cell, 'correct');
          this.chompPop();
          break;
        case 'munchDecoy':
          this.score += ev.value ?? 0;
          scored = true;
          this.placeGlyph(ev.cell, 'decoy');
          this.chompPop();
          break;
        case 'passArm':
          this.placeGlyph(ev.cell, 'threaded');
          break;
        case 'gauntlet':
          this.threadSpark(ev.cell);
          break;
      }
    }
    if (moved) this.lastMoveMs = simTimeMs;
    if (moved && targetCell !== this.curCell) {
      this.curCell = targetCell;
      this.moveTo(targetCell);
    }
    if (scored) this.layoutChip();
    // Idle-dim: while parked >IDLE_DIM_MS ease the body toward the fainter alpha so it stops bleeding
    // over the answer text under it; restore toward the moving alpha once it steps again. Motion-only —
    // reduced-motion keeps the flat constant set in the constructor.
    if (motionAllowed()) {
      const target = simTimeMs - this.lastMoveMs > IDLE_DIM_MS ? GHOST_ALPHA_IDLE : GHOST_ALPHA_MOVING;
      this.body.setAlpha(this.body.alpha + (target - this.body.alpha) * 0.15);
    }
    this.maybeTaunt(simTimeMs, humanScore);
  }

  /**
   * Ghost taunt triggers (Slice D): one-shot race-start + one-shot finish (when the whole track has
   * played out), plus edge-triggered lead flips vs the LIVE human score — the ghost's own chip score
   * (`this.score`, the cumulative recorded munch value the chip shows) crosses the human's. Flips are
   * min-gap throttled so a see-saw board doesn't chatter; start/finish are singular so they bypass the
   * throttle but each fire once. Captions show even under mute; missing humanScore only skips lead flips.
   */
  private maybeTaunt(simTimeMs: number, humanScore?: number): void {
    if (!this.tauntedStart) {
      this.tauntedStart = true;
      this.emitTaunt('raceStart', simTimeMs);
      return; // don't stack a lead flip onto the opener the same frame
    }
    if (!this.tauntedFinish && this.cursor >= this.track.events.length) {
      this.tauntedFinish = true;
      this.emitTaunt('ghostFinishes', simTimeMs);
      return;
    }
    if (humanScore === undefined) return;
    const gap = this.score - humanScore;
    const sign = gap > LEAD_GAP ? 1 : gap < -LEAD_GAP ? -1 : 0;
    if (sign !== 0 && sign !== this.lastLeadSign) {
      this.lastLeadSign = sign; // track the latest lead immediately, even if we throttle the voice
      if (simTimeMs - this.lastTauntMs >= TAUNT_MIN_GAP_MS) {
        this.emitTaunt(sign === 1 ? 'ghostLeads' : 'playerPasses', simTimeMs);
      }
    }
  }

  /** Fire one ghost caption near the score chip (position-separate from BYT3's readout caption). */
  private emitTaunt(moment: TauntMoment, simTimeMs: number): void {
    this.lastTauntMs = simTimeMs;
    spawnGhostCaption(this.scene, this.chipAnchor.x, this.chipAnchor.y + 22, pickTaunt(this.tauntName, moment), this.tauntAccent);
  }

  /**
   * GHOST DUELS Slice B (late-fetch fix): fast-forward to `simTimeMs` at BUILD time when a remote ghost
   * arrived after the board already advanced. Fires every past event INSTANTLY — glyphs painted with no
   * pop tween, position snapped, score/chip set — WITHOUT the per-event chomp pop or thread spark, so a
   * catch-up never dumps a burst of pops/sparks in one frame. Normal update() then resumes from the same
   * cursor and only animates FUTURE events. Reduced-motion is unaffected (it never tweens anyway).
   */
  catchUp(simTimeMs: number): void {
    if (this.destroyed) return;
    // The race is already mid-flight for a late-fetched remote ghost — skip the "is watching…" opener
    // (update() would otherwise fire it on the next tick as if the race were just starting).
    this.tauntedStart = true;
    let targetCell = this.curCell;
    let moved = false;
    while (this.cursor < this.track.events.length && this.track.events[this.cursor].t <= simTimeMs) {
      const ev = this.track.events[this.cursor];
      this.cursor += 1;
      switch (ev.kind) {
        case 'move':
          targetCell = ev.cell;
          moved = true;
          break;
        case 'munchCorrect':
          this.score += ev.value ?? 0;
          this.placeGlyph(ev.cell, 'correct', false);
          break;
        case 'munchDecoy':
          this.score += ev.value ?? 0;
          this.placeGlyph(ev.cell, 'decoy', false);
          break;
        case 'passArm':
          this.placeGlyph(ev.cell, 'threaded', false);
          break;
        case 'gauntlet':
          break; // the thread spark is a motion beat — skipped on catch-up
      }
    }
    if (moved) {
      this.curCell = targetCell;
      const c = this.cellCenter(targetCell);
      this.body.setPosition(c.x, c.y); // snap, no tween
      this.cellRing.setPosition(c.x, c.y); // M7: marker snaps with the fast-forwarded ghost
    }
    this.lastMoveMs = simTimeMs;
    this.layoutChip();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.tweens.killTweensOf(this.body);
    if (this.art) this.scene.tweens.killTweensOf(this.art);
    for (const o of this.owned) this.scene.tweens.killTweensOf(o);
    this.root.destroy(true);
    this.chip.destroy(true);
  }

  // --- internals ----------------------------------------------------------------------------------

  private moveTo(cell: number): void {
    const c = this.cellCenter(cell);
    this.cellRing.setPosition(c.x, c.y); // M7: the tile marker snaps to the new cell (marks WHERE, not travel)
    if (!motionAllowed()) {
      this.body.setPosition(c.x, c.y);
      return;
    }
    this.scene.tweens.killTweensOf(this.body); // kill only the prior POSITION tween (art is separate)
    this.scene.tweens.add({ targets: this.body, x: c.x, y: c.y, duration: 130, ease: 'Cubic.easeOut' });
  }

  private chompPop(): void {
    if (!motionAllowed() || !this.art || !this.art.active) return;
    const s = this.artBaseScale;
    const art = this.art;
    this.scene.tweens.killTweensOf(art);
    this.scene.tweens.add({
      targets: art,
      scaleX: s * 1.18,
      scaleY: s * 0.9,
      duration: 90,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (art.active) art.setScale(s);
      },
    });
  }

  private placeGlyph(cell: number, role: GlyphRole, animate = true): void {
    const c = this.cellCenter(cell);
    const size = Math.max(11, Math.min(16, Math.floor(this.cellW * 0.26)));
    const glyph = role === 'correct' ? '✓' : role === 'decoy' ? '✗' : '◇'; // ✓ / ✗ / ◇
    const t = this.scene.add
      .text(c.x + this.cellW * 0.3, c.y + this.cellH * 0.28, glyph, {
        fontFamily: FONT.family,
        fontSize: `${size}px`,
        color: hex(ROLE_COLOR[role]),
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0.5);
    this.glyphLayer.add(t);
    this.owned.push(t);
    // `animate=false` (catch-up) paints the glyph at its resting state with NO pop tween.
    if (animate && motionAllowed()) {
      t.setScale(0.4).setAlpha(0);
      this.scene.tweens.add({ targets: t, scale: 1, alpha: 0.5, duration: 200, ease: 'Back.easeOut' });
    }
  }

  /** THREADED ×1.5 marker beat: a brief amber ring bloom at the cell (motion-only, self-cleaning). */
  private threadSpark(cell: number): void {
    if (!motionAllowed()) return;
    const c = this.cellCenter(cell);
    const r = this.munchRadius * 0.9;
    const ring = this.scene.add
      .circle(c.x, c.y + this.spriteYOffset, r * 0.7)
      .setStrokeStyle(2, PALETTE.armed, 1)
      .setFillStyle()
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.6);
    this.glyphLayer.add(ring);
    this.owned.push(ring);
    this.scene.tweens.add({
      targets: ring,
      radius: r * 1.5,
      alpha: 0,
      duration: 320,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (ring.active) ring.destroy();
      },
    });
  }

  private layoutChip(): void {
    const padX = 10;
    const gap = 6;
    this.chipScore.setText(this.score.toLocaleString('en-US'));
    this.chipScore.setX(-padX);
    const scoreW = this.chipScore.width;
    this.chipName.setX(-padX - scoreW - gap);
    const contentW = this.chipName.width + gap + scoreW;
    const pillW = contentW + padX * 2;
    const pillH = Math.max(24, this.chipScore.height + 8);
    this.chipBg.clear();
    this.chipBg.fillStyle(PALETTE.surface, 0.92);
    this.chipBg.fillRoundedRect(-pillW, -pillH / 2, pillW, pillH, pillH / 2);
    this.chipBg.lineStyle(1.5, GHOST_ACCENT, 0.7);
    this.chipBg.strokeRoundedRect(-pillW, -pillH / 2, pillW, pillH, pillH / 2);
  }
}
